import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import Icon from "components/components/Icon/Icon";
import { IconSize } from "components/components/Icon/IconWrapper";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import featureFlags from "utils/featureFlags";

import {
  DropdownContainer,
  DropdownIconWrapper,
  DropdownNavItem,
  DropdownTitle,
  OldNavButton,
  OldStyledLink,
  StyledNavButton,
  StyledLink,
} from "./style";

interface Props {
  linkText: string;
  isActive: boolean;
  linkTo?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  onMouseLeave?: (navButtonId: string) => void;
  isDropdownOpen?: boolean;
  external?: boolean;
}

const NavLink: React.FC<Props> = ({
  linkText,
  linkTo,
  isActive,
  children,
  onClick = (): void => {},
  onMouseLeave = (navButtonId): void => {
    document.getElementById(navButtonId)?.blur();
  },
  isDropdownOpen,
  external,
}) => {
  const navigate = useNavigate();

  const linkTextReplace = linkText.replace(/\s/g, "-").toLowerCase();
  const navLinkTestIdSpaceless = `nav-link-${linkTextReplace}`;
  const navButtonIdSpaceless = `nav-button-${linkTextReplace}`;
  const dropdownId = `nav-dropdown-${linkTextReplace}`;

  const StyledLinkComponent = featureFlags.rebrand ? StyledLink : OldStyledLink;
  const NavButtonComponent = featureFlags.rebrand
    ? StyledNavButton
    : OldNavButton;
  const DropdownNavItemComponent = featureFlags.rebrand
    ? DropdownNavItem
    : "li";

  useEffect(() => {
    if (!isDropdownOpen) {
      document.getElementById(navButtonIdSpaceless)?.blur();
    }
  }, [navButtonIdSpaceless, isDropdownOpen]);

  if (children) {
    return (
      <DropdownNavItemComponent
        className={classnames("navbar-item has-dropdown", {
          "is-hoverable": !featureFlags.rebrand,
          "is-open": isDropdownOpen,
        })}
        onMouseLeave={(): void => onMouseLeave(navButtonIdSpaceless)}
      >
        <StyledLinkComponent
          className="navbar-link is-arrowless"
          data-test-id={navLinkTestIdSpaceless}
          as="div"
        >
          <NavButtonComponent
            type="button"
            id={navButtonIdSpaceless}
            className={classnames({ active: isActive })}
            aria-expanded={isDropdownOpen}
            aria-controls={dropdownId}
            onClick={onClick}
          >
            {linkText}
            {featureFlags.rebrand && (
              <DropdownIconWrapper>
                <Icon
                  iconName={faChevronDown}
                  colour="white"
                  size={IconSize.small}
                />
              </DropdownIconWrapper>
            )}
          </NavButtonComponent>
        </StyledLinkComponent>

        {featureFlags.rebrand ? (
          <DropdownContainer className="navbar-dropdown" id={dropdownId}>
            <DropdownTitle>{linkText}</DropdownTitle>
            {children}
          </DropdownContainer>
        ) : (
          <div className="navbar-dropdown" id={dropdownId}>
            {children}
          </div>
        )}
      </DropdownNavItemComponent>
    );
  }

  return linkTo ? (
    <StyledLinkComponent
      className="navbar-item"
      onClick={(): void => {
        if (external) {
          window.open(linkTo);
        } else {
          navigate(linkTo);
          onClick();
        }
      }}
      data-test-id={navLinkTestIdSpaceless}
    >
      <NavButtonComponent
        type="button"
        id={navButtonIdSpaceless}
        className={classnames({ active: isActive })}
      >
        {linkText}
      </NavButtonComponent>
    </StyledLinkComponent>
  ) : null;
};

export default NavLink;
